import { useCallback, useRef } from 'react';
import { COMPANY_PROFILE_CONFIG, PROFILE_REQUESTS } from 'features/inventory-profiles/constants';
import { buildProfileEndpoint } from 'features/inventory-profiles/utils';
import { useCreateProfileView } from 'hooks/createProfileView';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import API from 'services/API';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';

const INITIAL_STATE = { company: {}, recruiterRoles: [] };

const getResponseData = response => response.data?.items ?? response.data;

const fetchCompanyProfile = async companyEndpoint => {
  const company = getResponseData(await API.get(companyEndpoint, { apiVersion: 2 }));
  const recruiterId = company.recruiter?.id;
  if (!recruiterId) return { data: { company, recruiterRoles: [] } };

  try {
    const recruiterRoles =
      getResponseData(await API.get(Endpoints.UserRoles.replace(':id', recruiterId))) ?? [];
    return { data: { company, recruiterRoles } };
  } catch {
    return { data: { company, recruiterRoles: null } };
  }
};

/**
 * Company profile and its recruiter's roles, loaded as one operation so a company is never paired
 * with the roles of a previous recruiter. A roles failure keeps the profile on screen and returns
 * `recruiterRoles: null` (unknown), so the permissions turn editing off instead of the page failing.
 * @param {number|string} id Company id
 * @returns {{
 *   company: Object,
 *   recruiterRoles: Array<{ id: number }> | null,
 *   error: { request: string, statusCode?: number } | null,
 *   isAlreadyAccountable: boolean,
 *   isLoading: boolean,
 *   refresh: () => void,
 *   reload: () => void
 * }}
 */
export const useCompanyProfile = id => {
  const [user] = useUserContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const latestRequestRef = useRef(null);

  const companyEndpoint = buildProfileEndpoint({
    entity: COMPANY_PROFILE_CONFIG,
    id,
    isEnabled: checkIfFeatureFlagEnabled
  });

  const loadProfile = useCallback(() => {
    const profileRequest = fetchCompanyProfile(companyEndpoint);
    latestRequestRef.current = profileRequest;
    const resolveWithLatestRequest = () => latestRequestRef.current;
    return profileRequest.then(resolveWithLatestRequest, resolveWithLatestRequest);
  }, [companyEndpoint]);

  const { state, refreshData } = useFetchWithStatusV2({
    promise: id ? loadProfile : undefined,
    initialState: INITIAL_STATE
  });

  const { status, results, responseStatusCode } = state;
  const isLoading = status === UIStatus.Loading;
  const { company, recruiterRoles } = isLoading ? INITIAL_STATE : results;

  useCreateProfileView({ id: company.id, endpoint: COMPANY_PROFILE_CONFIG.trackingUrl });

  return {
    company,
    error:
      status === UIStatus.Error
        ? { request: PROFILE_REQUESTS.Profile, statusCode: responseStatusCode }
        : null,
    isAlreadyAccountable: company.recruiter?.id === user.id,
    isLoading,
    recruiterRoles,
    refresh: () => refreshData(false),
    reload: () => refreshData()
  };
};
